import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Phone, Mail, Clock, Shield, Star, Users, Award, Zap, Eye, AlertTriangle, Droplets, Wind, Camera, FileText, MessageCircle, Calendar, MapPin } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
const Index = () => {
  const {
    toast
  } = useToast();
  const [formData, setFormData] = useState({
    nome: '',
    nomeBnb: '',
    indirizzo: '',
    numeroSplit: '',
    telefono: '',
    email: '',
    preferenzaContatto: ''
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validazione base
    if (!formData.nome || !formData.nomeBnb || !formData.telefono || !formData.email) {
      toast({
        title: "Campi obbligatori mancanti",
        description: "Compila tutti i campi richiesti per procedere.",
        variant: "destructive"
      });
      return;
    }

    // Simula invio form
    toast({
      title: "Richiesta inviata con successo!",
      description: "Ti contatteremo entro 15 minuti per fissare il Check Gratuito."
    });

    // Reset form
    setFormData({
      nome: '',
      nomeBnb: '',
      indirizzo: '',
      numeroSplit: '',
      telefono: '',
      email: '',
      preferenzaContatto: ''
    });
  };
  const handleWhatsApp = () => {
    const message = encodeURIComponent('Ciao! Vorrei prenotare il Check Muffa Gratuito per il mio B&B.');
    window.open(`https://wa.me/390510828717?text=${message}`, '_blank');
  };
  const scrollToForm = () => {
    document.getElementById('form-contatto')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen bg-background">
      {/* SEZIONE 1 - HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary via-primary to-trust text-white">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative section-padding">
          <div className="container-max">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-8">
                <div className="space-y-4">
                  <Badge className="bg-white/20 text-white border-white/30 hover:bg-white/30">
                    <Shield className="w-4 h-4 mr-2" />
                    Check Gratuito Bologna
                  </Badge>
                  <h1 className="text-4xl lg:text-6xl font-bold leading-tight">
                    Il 90% dei condizionatori dei B&B contiene{' '}
                    <span className="text-warning">muffe invisibili</span>{' '}
                    che scopri solo quando un ospite lo scrive in una recensione.
                  </h1>
                  <p className="text-xl lg:text-2xl text-white/90 leading-relaxed">
                    Richiedi ora il <strong>Check Muffa Gratuito</strong> – dura 15 minuti e ti mostra lo stato reale dei tuoi split.
                  </p>
                </div>
                
                <div className="grid sm:grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-5 h-5 text-green-300" />
                    <span>Zero costi</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-5 h-5 text-green-300" />
                    <span>Zero impegno</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-5 h-5 text-green-300" />
                    <span>Report foto/video su WhatsApp</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-5 h-5 text-green-300" />
                    <span>Tecnici certificati Ariapura</span>
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button onClick={scrollToForm} size="lg" className="bg-white text-primary hover:bg-white/90 font-semibold px-8 py-4 text-lg shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300">
                    <Calendar className="w-5 h-5 mr-2" />
                    Prenota il Check Gratuito
                  </Button>
                  <Button onClick={handleWhatsApp} variant="outline" size="lg" className="border-white/30 text-white hover:bg-white/10 font-semibold px-8 py-4 text-lg">
                    <MessageCircle className="w-5 h-5 mr-2" />
                    Scrivici su WhatsApp
                  </Button>
                </div>
              </div>
              
              <div className="relative">
                <img src="/images/bnb_camera_2.jpeg" alt="Camera B&B elegante" className="rounded-2xl shadow-2xl" />
                <div className="absolute -bottom-6 -right-6 bg-white text-primary p-4 rounded-xl shadow-xl">
                  <div className="text-2xl font-bold">15 min</div>
                  <div className="text-sm">Check Gratuito</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SEZIONE 2 - IL PROBLEMA */}
      <section className="section-padding bg-red-50">
        <div className="container-max">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Il vero problema non è la polvere: è la{' '}
              <span className="text-red-600">muffa interna invisibile</span>.
            </h2>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <p className="text-lg text-gray-700 leading-relaxed">
                Nel <strong className="text-red-600">90% dei B&B</strong> che visitiamo troviamo muffe che:
              </p>
              
              <div className="space-y-4">
                {[{
                icon: Eye,
                text: "non si vedono ad occhio nudo"
              }, {
                icon: Clock,
                text: "si formano quando il condizionatore rimane fermo"
              }, {
                icon: Wind,
                text: "causano cattivi odori"
              }, {
                icon: Star,
                text: 'generano recensioni negative come "aria pesante", "camera sporca", "odore sgradevole"'
              }].map((item, index) => <div key={index} className="flex items-start space-x-3">
                    <div className="bg-red-100 p-2 rounded-lg">
                      <item.icon className="w-5 h-5 text-red-600" />
                    </div>
                    <p className="text-gray-700">{item.text}</p>
                  </div>)}
              </div>
              
              <div className="bg-white p-6 rounded-xl border-l-4 border-red-500 shadow-lg">
                <p className="text-gray-800 font-medium">
                  Le pulizie ordinarie non rimuovono la muffa interna.<br />
                  La maggior parte dei manutentori non la controlla nemmeno.
                </p>
              </div>
            </div>
            
            <div className="relative">
              <img src="/images/condizionatore_muffa_1.jpeg" alt="Muffa interna condizionatore" className="rounded-2xl shadow-xl" />
              <div className="absolute top-4 right-4 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                Muffa Invisibile
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SEZIONE 3 - PERCHÉ SUCCEDE */}
      <section className="section-padding">
        <div className="container-max">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Perché anche un condizionatore "pulito" può fare cattivo odore?
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[{
            icon: Droplets,
            title: "Umidità interna",
            desc: "L'umidità residua crea l'ambiente perfetto per la proliferazione di muffe e batteri"
          }, {
            icon: Clock,
            title: "Condizionatore fermo per mesi",
            desc: "Durante i periodi di inattività, l'umidità stagnante favorisce la crescita microbica"
          }, {
            icon: AlertTriangle,
            title: "Biofilm e muffe nei punti non visibili",
            desc: "Si formano nelle parti interne non accessibili durante le pulizie ordinarie"
          }, {
            icon: Shield,
            title: "Nessuna sanificazione profonda",
            desc: "Le pulizie standard non raggiungono le parti interne dove si annida la muffa"
          }, {
            icon: Wind,
            title: "Scarico condensa sporco o ostruito",
            desc: "L'acqua stagnante diventa un focolaio di batteri e cattivi odori"
          }, {
            icon: Eye,
            title: "Controlli superficiali",
            desc: "La maggior parte dei controlli si limita ai filtri esterni, ignorando l'interno"
          }].map((item, index) => <Card key={index} className="hover:shadow-lg transition-shadow duration-300">
                <CardHeader className="text-center">
                  <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <item.icon className="w-8 h-8 text-primary" />
                  </div>
                  <CardTitle className="text-lg">{item.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-center">{item.desc}</p>
                </CardContent>
              </Card>)}
          </div>
        </div>
      </section>

      {/* SEZIONE 4 - LA SOLUZIONE: CHECK GRATUITO */}
      <section className="section-padding bg-gradient-to-br from-primary/5 to-trust/5">
        <div className="container-max">
          <div className="text-center mb-12">
            <Badge className="bg-trust text-white mb-4">
              <CheckCircle className="w-4 h-4 mr-2" />
              Soluzione Professionale
            </Badge>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Ecco cosa include il <span className="gradient-text">Check Muffa Gratuito</span>
            </h2>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              {[{
              icon: Eye,
              title: "Ispezione interna dello split",
              desc: "Controllo approfondito delle parti interne non visibili"
            }, {
              icon: Camera,
              title: "Foto e video della muffa, se presente",
              desc: "Documentazione fotografica completa dello stato igienico"
            }, {
              icon: Award,
              title: "Valutazione igienica 0–10 (metodo Ariapura)",
              desc: "Sistema di valutazione proprietario per quantificare lo stato"
            }, {
              icon: Shield,
              title: "Controllo filtri",
              desc: "Verifica dello stato e dell'efficacia dei filtri"
            }, {
              icon: Wind,
              title: "Verifica odori",
              desc: "Test olfattivo per identificare problemi non visibili"
            }, {
              icon: Droplets,
              title: "Controllo scarico condensa",
              desc: "Ispezione del sistema di drenaggio"
            }, {
              icon: FileText,
              title: "Report completo via WhatsApp",
              desc: "Documentazione dettagliata inviata immediatamente"
            }, {
              icon: Users,
              title: "Consigli personalizzati",
              desc: "Raccomandazioni specifiche per la tua struttura"
            }].map((item, index) => <div key={index} className="flex items-start space-x-4">
                  <div className="bg-primary/10 p-3 rounded-lg flex-shrink-0">
                    <item.icon className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{item.title}</h3>
                    <p className="text-gray-600">{item.desc}</p>
                  </div>
                </div>)}
            </div>
            
            <div className="relative">
              <img src="/images/tecnico_ariapura_1.jpeg" alt="Tecnico Ariapura al lavoro" className="rounded-2xl shadow-xl" />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl">
                <div className="text-3xl font-bold text-primary mb-2">15 min</div>
                <div className="text-sm text-gray-600">Durata del check</div>
                <div className="text-sm font-medium text-trust">100% Gratuito</div>
              </div>
            </div>
          </div>
          
          <div className="text-center mt-12">
            <Button onClick={scrollToForm} size="lg" className="cta-button text-xl px-12 py-6">
              <Calendar className="w-6 h-6 mr-3" />
              Prenota il Check Gratuito
            </Button>
          </div>
        </div>
      </section>

      {/* SEZIONE 5 - PERCHÉ SCEGLIERE ARIAPURA */}
      <section className="section-padding">
        <div className="container-max">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Perché i B&B di Bologna scelgono <span className="gradient-text">Ariapura</span>
            </h2>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <div className="space-y-8">
              {[{
              icon: Award,
              title: "Specialisti nella sanificazione dei condizionatori delle strutture ricettive",
              desc: "Esperienza specifica nel settore hospitality con protocolli dedicati"
            }, {
              icon: Shield,
              title: "Tecnici certificati e strumentazione professionale",
              desc: "Team qualificato con attrezzature all'avanguardia per interventi efficaci"
            }, {
              icon: Zap,
              title: "Procedure rapide senza interrompere il lavoro del B&B",
              desc: "Interventi programmati per non disturbare gli ospiti e le operazioni"
            }, {
              icon: Camera,
              title: "Report fotografici trasparenti",
              desc: "Documentazione completa prima e dopo l'intervento"
            }, {
              icon: Star,
              title: "Riduzione immediata dei reclami degli ospiti sugli odori",
              desc: "Risultati misurabili sulla qualità dell'aria e soddisfazione ospiti"
            }].map((item, index) => <div key={index} className="flex items-start space-x-4">
                  <div className="bg-trust/10 p-3 rounded-lg flex-shrink-0">
                    <item.icon className="w-6 h-6 text-trust" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-gray-600">{item.desc}</p>
                  </div>
                </div>)}
            </div>
            
            <div className="relative">
              <img src="/images/tecnico_ariapura_2.jpeg" alt="Tecnico specializzato Ariapura" className="rounded-2xl shadow-xl" />
            </div>
          </div>
          
          {/* Numeri */}
          <div className="grid md:grid-cols-3 gap-8">
            {[{
            number: "+1200",
            label: "impianti sanificati",
            icon: Shield
          }, {
            number: "+180",
            label: "strutture servite",
            icon: Users
          }, {
            number: "+40",
            label: "property manager collaborano con noi",
            icon: Award
          }].map((stat, index) => <Card key={index} className="text-center hover:shadow-lg transition-shadow duration-300">
                <CardContent className="pt-8">
                  <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <stat.icon className="w-8 h-8 text-primary" />
                  </div>
                  <div className="text-4xl font-bold text-primary mb-2">{stat.number}</div>
                  <div className="text-gray-600">{stat.label}</div>
                </CardContent>
              </Card>)}
          </div>
        </div>
      </section>

      {/* SEZIONE 6 - COME FUNZIONA */}
      <section className="section-padding bg-gray-50">
        <div className="container-max">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Come funziona il servizio in <span className="gradient-text">3 semplici step</span>
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[{
            step: "1",
            title: "Prenotazione",
            desc: "Compili il modulo o ci scrivi su WhatsApp.",
            icon: MessageCircle
          }, {
            step: "2",
            title: "Check in struttura",
            desc: "In 15 minuti ti mostriamo foto e video reali.",
            icon: Eye
          }, {
            step: "3",
            title: "Preventivo immediato",
            desc: "Se vuoi, ricevi il preventivo personalizzato dopo il check.",
            icon: FileText
          }].map((item, index) => <div key={index} className="relative">
                <Card className="text-center hover:shadow-lg transition-shadow duration-300 h-full">
                  <CardContent className="pt-8">
                    <div className="bg-gradient-to-r from-primary to-trust w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold">
                      {item.step}
                    </div>
                    <div className="bg-primary/10 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                      <item.icon className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">{item.title}</h3>
                    <p className="text-gray-600">{item.desc}</p>
                  </CardContent>
                </Card>
                {index < 2 && <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <div className="w-8 h-0.5 bg-gradient-to-r from-primary to-trust"></div>
                  </div>}
              </div>)}
          </div>
          
          <div className="text-center mt-12">
            <Button onClick={scrollToForm} size="lg" className="cta-button text-xl px-12 py-6">
              <Calendar className="w-6 h-6 mr-3" />
              Prenota ora il Check Gratuito
            </Button>
          </div>
        </div>
      </section>

      {/* SEZIONE 7 - DOPO IL CHECK */}
      <section className="section-padding">
        <div className="container-max">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Dopo il check puoi scegliere tra <span className="gradient-text">due opzioni</span>:
            </h2>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Opzione 1 */}
            <Card className="hover:shadow-xl transition-shadow duration-300">
              <CardHeader className="text-center">
                <Badge className="bg-primary text-white w-fit mx-auto mb-4">
                  Opzione 1
                </Badge>
                <CardTitle className="text-2xl">Intervento singolo</CardTitle>
                <CardDescription className="text-lg">
                  Sanificazione professionale dello split.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-trust" />
                  <span>Sanificazione completa</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-trust" />
                  <span>Eliminazione muffa e batteri</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-trust" />
                  <span>Preventivo personalizzato</span>
                </div>
              </CardContent>
            </Card>
            
            {/* Opzione 2 */}
            <Card className="border-2 border-trust hover:shadow-xl transition-shadow duration-300 relative">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <Badge className="bg-trust text-white px-4 py-1">
                  Consigliato
                </Badge>
              </div>
              <CardHeader className="text-center pt-8">
                <Badge className="bg-trust text-white w-fit mx-auto mb-4">
                  Opzione 2
                </Badge>
                <CardTitle className="text-2xl">Programma B&B Aria Sicura</CardTitle>
                <CardDescription className="text-lg">
                  Manutenzione programmata per la massima tranquillità.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-trust" />
                  <span>2 sanificazioni/anno</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-trust" />
                  <span>Check gratuiti</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-trust" />
                  <span>Priorità interventi</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-trust" />
                  <span>Prezzo scontato</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-trust" />
                  <span>Certificato da mostrare agli ospiti</span>
                </div>
              </CardContent>
            </Card>
          </div>
          
          <div className="text-center mt-12">
            <Button onClick={scrollToForm} size="lg" className="cta-button text-xl px-12 py-6">
              <Calendar className="w-6 h-6 mr-3" />
              Richiedi il Check Gratuito
            </Button>
          </div>
        </div>
      </section>

      {/* SEZIONE 8 - TESTIMONIANZE */}
      <section className="section-padding bg-gradient-to-br from-trust/5 to-primary/5">
        <div className="container-max">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Cosa dicono i nostri clienti
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[{
            name: "Marco Rossi",
            business: "B&B Casa Bella, Bologna",
            text: "Dopo l'intervento di Ariapura, zero reclami sugli odori. I miei ospiti ora commentano positivamente l'aria fresca delle camere.",
            rating: 5
          }, {
            name: "Laura Bianchi",
            business: "Affittacamere Centro Storico",
            text: "Rapidità incredibile! In 15 minuti hanno fatto il check e in un'ora avevo già il preventivo. Servizio impeccabile.",
            rating: 5
          }, {
            name: "Giuseppe Verdi",
            business: "Property Manager, 12 strutture",
            text: "Collaboro con Ariapura da 2 anni. Tecnici sempre puntuali e affidabili. Il programma annuale mi fa risparmiare tempo e denaro.",
            rating: 5
          }].map((testimonial, index) => <Card key={index} className="hover:shadow-lg transition-shadow duration-300">
                <CardContent className="pt-6">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />)}
                  </div>
                  <p className="text-gray-700 mb-4 italic">"{testimonial.text}"</p>
                  <div>
                    <div className="font-semibold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-600">{testimonial.business}</div>
                  </div>
                </CardContent>
              </Card>)}
          </div>
        </div>
      </section>

      {/* SEZIONE 9 - FAQ */}
      <section className="section-padding">
        <div className="container-max">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Domande frequenti
            </h2>
          </div>
          
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {[{
              question: "Quanto dura il check?",
              answer: "Il Check Muffa Gratuito dura esattamente 15 minuti. I nostri tecnici sono molto efficienti e non interrompono le operazioni del tuo B&B."
            }, {
              question: "È davvero gratuito?",
              answer: "Sì, il check è completamente gratuito e senza alcun vincolo. Non c'è nessun costo nascosto e non sei obbligato ad acquistare nessun servizio."
            }, {
              question: "Può dare fastidio agli ospiti?",
              answer: "Assolutamente no. Il check viene sempre programmato quando la camera è libera, in accordo con i tuoi orari di check-in e check-out."
            }, {
              question: "Se non c'è muffa?",
              answer: "Anche se non troviamo muffa, ti consegniamo comunque il report completo con foto e la valutazione igienica. È sempre meglio avere la certezza!"
            }, {
              question: "Quanto costa la sanificazione?",
              answer: "Ti mandiamo un preventivo personalizzato immediatamente dopo il check, basato sulle condizioni reali dei tuoi condizionatori. Nessuna sorpresa, massima trasparenza."
            }].map((faq, index) => <AccordionItem key={index} value={`item-${index}`} className="border border-gray-200 rounded-lg px-6">
                  <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-primary">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-700">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </section>

      {/* SEZIONE FINALE - CTA */}
      <section className="section-padding bg-gradient-to-br from-primary via-primary to-trust text-white">
        <div className="container-max text-center">
          <div className="max-w-3xl mx-auto space-y-8">
            <Badge className="bg-white/20 text-white border-white/30">
              <Clock className="w-4 h-4 mr-2" />
              Disponibilità Limitata
            </Badge>
            
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight">
              Blocca ora il tuo <span className="text-warning">Check Gratuito</span>
            </h2>
            
            <p className="text-xl text-white/90">
              Disponibile solo per B&B di Bologna e provincia.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button onClick={scrollToForm} size="lg" className="bg-white text-primary hover:bg-white/90 font-semibold px-12 py-6 text-xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300">
                <Calendar className="w-6 h-6 mr-3" />
                Prenota adesso
              </Button>
              <Button onClick={handleWhatsApp} variant="outline" size="lg" className="border-white/30 text-white hover:bg-white/10 font-semibold px-12 py-6 text-xl">
                <MessageCircle className="w-6 h-6 mr-3" />
                Contattaci su WhatsApp
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* FORM DI CONTATTO */}
      <section id="form-contatto" className="section-padding bg-gray-50">
        <div className="container-max">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Prenota il tuo Check Muffa Gratuito
              </h2>
              <p className="text-gray-600">
                Compila il modulo e ti contatteremo entro 15 minuti per fissare l'appuntamento.
              </p>
            </div>
            
            <Card className="shadow-xl">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="nome">Nome e Cognome *</Label>
                      <Input id="nome" value={formData.nome} onChange={e => setFormData({
                      ...formData,
                      nome: e.target.value
                    })} placeholder="Mario Rossi" required />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="nomeBnb">Nome del B&B *</Label>
                      <Input id="nomeBnb" value={formData.nomeBnb} onChange={e => setFormData({
                      ...formData,
                      nomeBnb: e.target.value
                    })} placeholder="B&B Casa Bella" required />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="indirizzo">Indirizzo</Label>
                    <Input id="indirizzo" value={formData.indirizzo} onChange={e => setFormData({
                    ...formData,
                    indirizzo: e.target.value
                  })} placeholder="Via Roma 123, Bologna" />
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="numeroSplit">Numero di split</Label>
                      <Select value={formData.numeroSplit} onValueChange={value => setFormData({
                      ...formData,
                      numeroSplit: value
                    })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleziona" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1-3">1-3 split</SelectItem>
                          <SelectItem value="4-6">4-6 split</SelectItem>
                          <SelectItem value="7-10">7-10 split</SelectItem>
                          <SelectItem value="10+">Più di 10 split</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="telefono">Telefono *</Label>
                      <Input id="telefono" type="tel" value={formData.telefono} onChange={e => setFormData({
                      ...formData,
                      telefono: e.target.value
                    })} placeholder="+39 123 456 7890" required />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input id="email" type="email" value={formData.email} onChange={e => setFormData({
                    ...formData,
                    email: e.target.value
                  })} placeholder="mario@example.com" required />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="preferenzaContatto">Preferenza per il contatto</Label>
                    <Select value={formData.preferenzaContatto} onValueChange={value => setFormData({
                    ...formData,
                    preferenzaContatto: value
                  })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Quando preferisci essere contattato?" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mattina">Mattina (9:00-12:00)</SelectItem>
                        <SelectItem value="pomeriggio">Pomeriggio (14:00-18:00)</SelectItem>
                        <SelectItem value="sera">Sera (18:00-20:00)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <Button type="submit" className="w-full cta-button text-lg py-6">
                    <Calendar className="w-5 h-5 mr-2" />
                    Prenota il Check Gratuito
                  </Button>
                  
                  <p className="text-sm text-gray-600 text-center">
                    Ti contatteremo entro 15 minuti per confermare l'appuntamento.
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container-max">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-2xl font-bold mb-4 gradient-text">Ariapura</h3>
              <p className="text-gray-400 mb-4">
                Specialisti nella sanificazione professionale dei condizionatori per strutture ricettive.
              </p>
              <div className="flex space-x-4">
                <Button onClick={handleWhatsApp} variant="outline" size="sm" className="border-white/30 text-white hover:bg-white/10">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  WhatsApp
                </Button>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Servizi</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Check Muffa Gratuito</li>
                <li>Sanificazione Condizionatori</li>
                <li>Programma B&B Aria Sicura</li>
                <li>Manutenzione Programmata</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Contatti</h4>
              <div className="space-y-2 text-gray-400">
                <div className="flex items-center space-x-2">
                  <Phone className="w-4 h-4" />
                  <span>+39 051 08 28 717</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Mail className="w-4 h-4" />
                  <span>info@ariapura.o</span>
                </div>
                <div className="flex items-center space-x-2">
                  <MapPin className="w-4 h-4" />
                  <span>Bologna e Provincia</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 Ariapura. Tutti i diritti riservati.</p>
          </div>
        </div>
      </footer>
    </div>;
};
export default Index;